import React, { useState } from 'react';
import { createPortal } from 'react-dom';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (telegramUserId: string, clientName: string) => Promise<void>;
  loading: boolean;
}

export function AddClientModal({ isOpen, onClose, onAdd, loading }: AddClientModalProps) {
  const [telegramUserId, setTelegramUserId] = useState('');
  const [clientName, setClientName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (telegramUserId.trim() && clientName.trim()) {
      onAdd(telegramUserId.trim(), clientName.trim());
      setTelegramUserId('');
      setClientName('');
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="retro-card p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-text-dark">
            Add New Client
          </h3>
          <button
            onClick={onClose}
            className="text-text-medium hover:text-text-dark"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label 
              htmlFor="telegramUserId" 
              className="block text-sm font-medium text-text-medium mb-1"
            >
              Telegram User ID
            </label>
            <input
              id="telegramUserId"
              type="text"
              value={telegramUserId}
              onChange={(e) => setTelegramUserId(e.target.value)}
              className="vintage-input w-full"
              required
              placeholder="Enter Telegram User ID"
              aria-describedby="telegram-help"
            />
            <p id="telegram-help" className="text-xs text-text-medium mt-1">
              Ask the client to message <span className="font-semibold">@userinfobot</span> on Telegram to get their numeric ID.
            </p>
          </div>

          <div>
            <label 
              htmlFor="clientName" 
              className="block text-sm font-medium text-text-medium mb-1"
            >
              Client Name
            </label>
            <input
              id="clientName"
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="vintage-input w-full"
              required
              placeholder="Enter client's display name"
              aria-describedby="name-help"
            />
            <p id="name-help" className="text-xs text-text-medium mt-1">
              This is how the client will appear in your dashboard
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-text-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !telegramUserId.trim() || !clientName.trim()}
              className="flex-1 neon-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding...' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
} 